import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { hasSuperUserAccess } from '../utils/sessionUser';
import {
  LAYOUT_PRESETS,
  PALETTE_WIDGET_TYPES,
  CORE_FIELDS_LIMIT,
  createEmptyLayout,
  createRow,
  normalizeLayout,
  layoutEquals,
  layoutDisplayName,
  addWidgetToLayout,
  removeWidgetFromLayout,
  updateWidgetInLayout,
  moveWidgetInLayout,
  isMovePayload,
  widgetDragLabel,
} from '../utils/recordPageLayoutModel';
import { getCoreObjectFields } from '../utils/recordFieldDisplay';
import {
  RecordLayoutDragProvider,
  PaletteDraggable,
  CanvasWidgetDraggable,
  CanvasDropTarget,
} from '../components/recordPageLayout/RecordLayoutDnD';

export default function RecordPageLayoutBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const pendingNavActionRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [objects, setObjects] = useState([]);
  const [selectedObjectApiName, setSelectedObjectApiName] = useState(null);
  const [selectedObjectLabel, setSelectedObjectLabel] = useState('');
  const [layoutName, setLayoutName] = useState('');
  const [layout, setLayout] = useState(createEmptyLayout());
  const [savedLayout, setSavedLayout] = useState(createEmptyLayout());
  const [selectedLayoutKey, setSelectedLayoutKey] = useState('full');
  const [objectFields, setObjectFields] = useState([]);
  const [loadingObjects, setLoadingObjects] = useState(true);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showObjectDropdown, setShowObjectDropdown] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingObjectApiName, setPendingObjectApiName] = useState(null);
  const [nativePickPayload, setNativePickPayload] = useState(null);
  const [movingWidget, setMovingWidget] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);

  const isDirty = useMemo(
    () => !layoutEquals(layout, savedLayout),
    [layout, savedLayout]
  );

  const styles = useMemo(() => getStyles(theme), [theme]);

  useEffect(() => {
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
    } else if (route?.params?.userId) {
      fetch(`${getApiUrl()}/api/users/${route.params.userId}`)
        .then((res) => res.json())
        .then((data) => setCurrentUser(data))
        .catch((err) => console.error('Error fetching user:', err));
    }
  }, [route]);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') return undefined;
      let lockPromise;
      const lock = async () => {
        try {
          const ScreenOrientation = await import('expo-screen-orientation');
          if (ScreenOrientation?.lockAsync) {
            lockPromise = ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE);
            await lockPromise;
          }
        } catch {
          /* optional */
        }
      };
      lock();
      return () => {
        (async () => {
          try {
            const ScreenOrientation = await import('expo-screen-orientation');
            if (ScreenOrientation?.unlockAsync) await ScreenOrientation.unlockAsync();
          } catch {
            /* optional */
          }
        })();
      };
    }, [])
  );

  useEffect(() => {
    if (!navigation?.addListener) return undefined;
    const unsub = navigation.addListener('beforeRemove', (e) => {
      if (!isDirty) return;
      e.preventDefault();
      pendingNavActionRef.current = e.data.action;
      setShowUnsavedModal(true);
    });
    return unsub;
  }, [navigation, isDirty]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const fetchObjects = useCallback(async () => {
    setLoadingObjects(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/objects`);
      if (!res.ok) throw new Error('Failed to load objects');
      const data = await res.json();
      const formatted = (Array.isArray(data) ? data : []).map((obj) => ({
        value: obj.api_name,
        label: obj.label,
      }));
      setObjects(formatted);
      if (formatted.length > 0 && !selectedObjectApiName) {
        setSelectedObjectApiName(formatted[0].value);
        setSelectedObjectLabel(formatted[0].label);
      }
    } catch (err) {
      console.error(err);
      setObjects([]);
    } finally {
      setLoadingObjects(false);
    }
  }, [selectedObjectApiName]);

  useEffect(() => {
    fetchObjects();
  }, [fetchObjects]);

  const loadLayoutForObject = useCallback(async (apiName, label) => {
    if (!apiName) return;
    setLoadingLayout(true);
    try {
      const [layoutRes, fieldsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/objects/${encodeURIComponent(apiName)}/record-page-layout`),
        fetch(`${getApiUrl()}/api/objects/${encodeURIComponent(apiName)}/fields`),
      ]);
      const layoutData = layoutRes.ok ? await layoutRes.json() : null;
      const fieldsData = fieldsRes.ok ? await fieldsRes.json() : [];
      const normalized = normalizeLayout(layoutData?.layout_json);
      setLayout(normalized);
      setSavedLayout(normalizeLayout(layoutData?.layout_json));
      setLayoutName(layoutData?.name || layoutDisplayName(label, apiName));
      setObjectFields(Array.isArray(fieldsData) ? fieldsData : []);
    } catch (err) {
      console.error(err);
      const empty = createEmptyLayout();
      setLayout(empty);
      setSavedLayout(empty);
      setLayoutName(layoutDisplayName(label, apiName));
      setObjectFields([]);
    } finally {
      setLoadingLayout(false);
      setNativePickPayload(null);
    }
  }, []);

  useEffect(() => {
    if (!selectedObjectApiName) return;
    loadLayoutForObject(selectedObjectApiName, selectedObjectLabel);
  }, [selectedObjectApiName, selectedObjectLabel, loadLayoutForObject]);

  const requestObjectChange = (apiName, label) => {
    if (apiName === selectedObjectApiName) {
      setShowObjectDropdown(false);
      return;
    }
    if (isDirty) {
      setPendingObjectApiName(apiName);
      setShowUnsavedModal(true);
      return;
    }
    setSelectedObjectApiName(apiName);
    setSelectedObjectLabel(label);
    setShowObjectDropdown(false);
  };

  const applyPendingObjectChange = () => {
    if (pendingObjectApiName) {
      const obj = objects.find((o) => o.value === pendingObjectApiName);
      setSelectedObjectApiName(pendingObjectApiName);
      setSelectedObjectLabel(obj?.label || pendingObjectApiName);
      setPendingObjectApiName(null);
      setShowObjectDropdown(false);
    }
  };

  const handleSave = async (options = {}) => {
    if (!selectedObjectApiName || !currentUser?.id) {
      Alert.alert('Save failed', 'Select an object and ensure you are logged in.');
      return false;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(selectedObjectApiName)}/record-page-layout`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            layout_json: normalizeLayout(layout),
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      const data = await res.json();
      const normalized = normalizeLayout(data.layout_json);
      setLayout(normalized);
      setSavedLayout(normalizeLayout(data.layout_json));
      setLayoutName(data.name || layoutName);
      if (options.afterSave) options.afterSave();
      return true;
    } catch (err) {
      Alert.alert('Save failed', err.message || 'Could not save layout.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleApplyDefaultToAll = () => {
    if (!currentUser?.id) return;
    const confirm = () => {
      Alert.alert(
        'Apply to all objects',
        'This will overwrite every object’s record page layout with the current design. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Apply',
            style: 'destructive',
            onPress: async () => {
              setSaving(true);
              try {
                const res = await fetch(`${getApiUrl()}/api/record-page-layouts/apply-to-all`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    requester_id: currentUser.id,
                    layout_json: normalizeLayout(layout),
                  }),
                });
                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.error || 'Apply failed');
                }
                setSavedLayout(normalizeLayout(layout));
                Alert.alert('Success', 'Layout applied to all objects.');
              } catch (err) {
                Alert.alert('Apply failed', err.message || 'Could not apply layout.');
              } finally {
                setSaving(false);
              }
            },
          },
        ]
      );
    };
    if (Platform.OS === 'web') {
      if (window.confirm('Apply this layout to ALL objects? Existing layouts will be overwritten.')) {
        (async () => {
          setSaving(true);
          try {
            const res = await fetch(`${getApiUrl()}/api/record-page-layouts/apply-to-all`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                requester_id: currentUser.id,
                layout_json: normalizeLayout(layout),
              }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || 'Apply failed');
            }
            setSavedLayout(normalizeLayout(layout));
            window.alert('Layout applied to all objects.');
          } catch (err) {
            window.alert(err.message || 'Could not apply layout.');
          } finally {
            setSaving(false);
          }
        })();
      }
    } else {
      confirm();
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
      pendingNavActionRef.current = { type: 'DISCARD_AND_LEAVE' };
      return;
    }
    navigation.navigate('BuilderMain', {
      userId: currentUser?.id || route?.params?.userId || 1,
      user: currentUser,
    });
  };

  const handleUnsavedDiscard = () => {
    setLayout(savedLayout);
    setShowUnsavedModal(false);
    if (pendingObjectApiName) {
      applyPendingObjectChange();
      return;
    }
    const action = pendingNavActionRef.current;
    pendingNavActionRef.current = null;
    if (action?.type === 'DISCARD_AND_LEAVE') {
      navigation.navigate('BuilderMain', {
        userId: currentUser?.id || route?.params?.userId || 1,
        user: currentUser,
      });
      return;
    }
    if (action) navigation.dispatch(action);
  };

  const handleUnsavedSave = async () => {
    const ok = await handleSave({
      afterSave: () => {
        setShowUnsavedModal(false);
        if (pendingObjectApiName) {
          applyPendingObjectChange();
          return;
        }
        const action = pendingNavActionRef.current;
        pendingNavActionRef.current = null;
        if (action) navigation.dispatch(action);
      },
    });
    if (!ok) return;
  };

  const handleAddRow = () => {
    setLayout((prev) => ({
      ...normalizeLayout(prev),
      rows: [...normalizeLayout(prev).rows, createRow(selectedLayoutKey)],
    }));
  };

  const placeWidget = (rowId, columnIndex, payload) => {
    if (!payload?.type) return;
    setLayout((prev) => addWidgetToLayout(prev, rowId, columnIndex, payload));
    setNativePickPayload(null);
    setMovingWidget(null);
  };

  const handleDropOnColumn = (rowId, columnIndex, payload) => {
    if (isMovePayload(payload)) {
      const { widgetId, fromRowId, fromColumnIndex } = payload._move;
      setLayout((prev) =>
        moveWidgetInLayout(prev, fromRowId, fromColumnIndex, widgetId, rowId, columnIndex)
      );
      setMovingWidget(null);
      return;
    }
    placeWidget(rowId, columnIndex, payload);
  };

  const handleDropOnEmptyCanvas = (payload) => {
    if (isMovePayload(payload)) return;
    if (!payload?.type) return;
    const row = createRow(selectedLayoutKey);
    const next = { rows: [row] };
    setLayout(addWidgetToLayout(next, row.id, 0, payload));
    setNativePickPayload(null);
  };

  const handlePalettePress = (payload) => {
    setMovingWidget(null);
    setNativePickPayload((prev) =>
      prev && JSON.stringify(prev) === JSON.stringify(payload) ? null : payload
    );
  };

  const handleColumnPress = (rowId, columnIndex) => {
    if (movingWidget) {
      handleDropOnColumn(rowId, columnIndex, {
        ...movingWidget.widget,
        _move: {
          widgetId: movingWidget.widget.id,
          fromRowId: movingWidget.rowId,
          fromColumnIndex: movingWidget.columnIndex,
        },
      });
      return;
    }
    if (!nativePickPayload) return;
    placeWidget(rowId, columnIndex, nativePickPayload);
  };

  const handleBeginMoveWidget = (widget, rowId, columnIndex) => {
    setNativePickPayload(null);
    setMovingWidget({ widget, rowId, columnIndex });
  };

  const renderLayoutPreview = () => {
    const preset = LAYOUT_PRESETS[selectedLayoutKey] || LAYOUT_PRESETS.full;
    return (
      <View style={styles.layoutPreviewRow}>
        {preset.columns.map((frac, idx) => (
          <View
            key={`preview-${idx}`}
            style={[
              styles.layoutPreviewCell,
              { flex: frac },
              idx < preset.columns.length - 1 && styles.layoutPreviewCellGap,
            ]}
          >
            <Text style={styles.layoutPreviewLabel}>
              {frac === 1 ? '100%' : `${Math.round(frac * 100)}%`}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderWidget = (widget, rowId, columnIndex) => {
    const widgetTypeLabel =
      widget.type === 'field'
        ? 'Field'
        : widget.type === 'core-fields'
          ? 'Core fields'
          : widget.type;

    const corePreviewFields = getCoreObjectFields(objectFields, CORE_FIELDS_LIMIT);
    const isMoving =
      movingWidget?.widget?.id === widget.id &&
      movingWidget?.rowId === rowId &&
      movingWidget?.columnIndex === columnIndex;

    const handleRemove = () => {
      setLayout((prev) =>
        removeWidgetFromLayout(prev, rowId, columnIndex, widget.id)
      );
      if (movingWidget?.widget?.id === widget.id) setMovingWidget(null);
    };

    const handleEditPress = () => {
      if (widget.type === 'core-fields') return;
      setEditingWidget({ ...widget, rowId, columnIndex });
    };

    return (
      <CanvasWidgetDraggable
        key={widget.id}
        widget={widget}
        rowId={rowId}
        columnIndex={columnIndex}
        label={widgetDragLabel(widget)}
        style={[styles.canvasWidget, isMoving && styles.canvasWidgetMoving]}
        onPress={handleEditPress}
        onLongPress={() => handleBeginMoveWidget(widget, rowId, columnIndex)}
      >
        <View style={styles.widgetHeader}>
          <Text style={styles.widgetType}>{widgetTypeLabel}</Text>
          {Platform.OS === 'web' ? (
            React.createElement(
              'span',
              {
                'data-rpl-widget-remove': 'true',
                style: {
                  cursor: 'pointer',
                  padding: '4px 8px',
                  fontSize: 14,
                  color: '#c62828',
                  fontWeight: '600',
                },
                onPointerDown: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove();
                },
              },
              '✕'
            )
          ) : (
            <TouchableOpacity
              onPress={handleRemove}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.widgetRemove}>✕</Text>
            </TouchableOpacity>
          )}
        </View>
        {widget.type === 'section' && (
          <Text style={styles.widgetTitle}>{widget.label || 'Section'}</Text>
        )}
        {widget.type === 'core-fields' && (
          <>
            <Text style={styles.widgetTitle}>{widget.label || 'Core fields'}</Text>
            <Text style={styles.widgetMeta}>
              First {CORE_FIELDS_LIMIT} fields
              {corePreviewFields.length > 0
                ? `: ${corePreviewFields.map((f) => f.label).join(', ')}`
                : ' (loads from object schema)'}
            </Text>
          </>
        )}
        {widget.type === 'field' && (
          <>
            <Text style={styles.widgetTitle}>{widget.label || widget.fieldName}</Text>
            <Text style={styles.widgetMeta}>{widget.fieldName}</Text>
          </>
        )}
        {widget.type === 'text' && (
          <Text style={styles.widgetBody}>{widget.content || 'Text block'}</Text>
        )}
        {widget.type === 'image' && (
          <Text style={styles.widgetBody}>
            {widget.imageUrl ? widget.imageUrl : 'Image (no URL)'}
          </Text>
        )}
      </CanvasWidgetDraggable>
    );
  };

  if (currentUser && !hasSuperUserAccess(currentUser)) {
    return (
      <View style={styles.container}>
        <Text style={styles.deniedText}>Super-user access required.</Text>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Text style={styles.linkText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      data-screen="record-page-layout-builder"
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'record-page-layout-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={currentUser}
          />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Record Page Layout Builder</Text>
          {layoutName ? (
            <Text style={styles.layoutNameSub}>{layoutName}</Text>
          ) : null}
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <View style={styles.objectSelectorRow}>
        <Text style={styles.objectSelectorLabel}>Object</Text>
        <TouchableOpacity
          style={styles.objectSelectorTrigger}
          onPress={() => setShowObjectDropdown((v) => !v)}
          activeOpacity={0.85}
        >
          <Text style={styles.objectSelectorValue}>
            {selectedObjectLabel || selectedObjectApiName || 'Select object…'}
          </Text>
          <Text style={styles.objectSelectorChevron}>▾</Text>
        </TouchableOpacity>
        {isDirty ? <Text style={styles.unsavedBadge}>Unsaved changes</Text> : null}
      </View>

      {showObjectDropdown ? (
        <View style={styles.objectDropdown}>
          <ScrollView style={styles.objectDropdownScroll} nestedScrollEnabled>
            {objects.map((obj) => (
              <TouchableOpacity
                key={obj.value}
                style={[
                  styles.objectDropdownItem,
                  obj.value === selectedObjectApiName && styles.objectDropdownItemActive,
                ]}
                onPress={() => requestObjectChange(obj.value, obj.label)}
              >
                <Text style={styles.objectDropdownItemText}>{obj.label}</Text>
                <Text style={styles.objectDropdownItemMeta}>{obj.value}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      ) : null}

      <RecordLayoutDragProvider
        onDropEmpty={handleDropOnEmptyCanvas}
        onDropColumn={handleDropOnColumn}
      >
      <View style={styles.mainRow}>
        <View style={styles.leftPane}>
          <ScrollView style={styles.leftScroll} showsVerticalScrollIndicator={false}>
            <Text style={styles.panelSectionTitle}>Layout</Text>
            <Text style={styles.panelHint}>New rows use this column split</Text>
            {Object.values(LAYOUT_PRESETS).map((preset) => (
              <TouchableOpacity
                key={preset.key}
                style={[
                  styles.layoutOption,
                  selectedLayoutKey === preset.key && styles.layoutOptionActive,
                ]}
                onPress={() => setSelectedLayoutKey(preset.key)}
              >
                <Text style={styles.layoutOptionLabel}>{preset.label}</Text>
              </TouchableOpacity>
            ))}
            {renderLayoutPreview()}

            <TouchableOpacity style={styles.addRowBtn} onPress={handleAddRow}>
              <Text style={styles.addRowBtnText}>+ Add row to canvas</Text>
            </TouchableOpacity>

            {Platform.OS === 'web' ? (
              <Text style={styles.panelHint}>
                Press and hold to drag widgets from the palette or within the canvas.
              </Text>
            ) : null}
            {(nativePickPayload || movingWidget) && Platform.OS !== 'web' ? (
              <Text style={styles.nativePickHint}>
                {movingWidget
                  ? `Tap a column to move “${widgetDragLabel(movingWidget.widget)}”`
                  : `Tap a column to place “${nativePickPayload?.label || nativePickPayload?.type}”`}
              </Text>
            ) : null}
            {Platform.OS === 'web' && nativePickPayload ? (
              <Text style={styles.nativePickHint}>
                Drop “{nativePickPayload.label || nativePickPayload.type}” on the canvas, or click a column
              </Text>
            ) : null}
            <Text style={[styles.panelSectionTitle, styles.panelSectionSpaced]}>Widgets</Text>
            {PALETTE_WIDGET_TYPES.map((item) => {
              const payload = { type: item.type };
              return (
                <PaletteDraggable
                  key={item.type}
                  payload={payload}
                  label={item.label}
                  style={[
                    styles.paletteItem,
                    nativePickPayload?.type === item.type && styles.paletteItemActive,
                  ]}
                  active={nativePickPayload?.type === item.type}
                  activeStyle={styles.paletteItemActive}
                  onPress={() => handlePalettePress(payload)}
                >
                  <View style={styles.paletteItemInner}>
                    <Text style={styles.paletteIcon}>{item.icon}</Text>
                    <Text style={styles.paletteLabel}>{item.label}</Text>
                  </View>
                </PaletteDraggable>
              );
            })}

            <Text style={[styles.panelSectionTitle, styles.panelSectionSpaced]}>Fields</Text>
            {loadingLayout ? (
              <ActivityIndicator color="#d4af37" style={styles.fieldsLoading} />
            ) : objectFields.length === 0 ? (
              <Text style={styles.panelHint}>No fields for this object</Text>
            ) : (
              objectFields.map((field) => {
                const payload = {
                  type: 'field',
                  fieldName: field.field_name,
                  label: field.label || field.field_name,
                };
                return (
                  <PaletteDraggable
                    key={field.field_name}
                    payload={payload}
                    label={field.label || field.field_name}
                    style={[
                      styles.paletteItem,
                      nativePickPayload?.fieldName === field.field_name &&
                        styles.paletteItemActive,
                    ]}
                    active={nativePickPayload?.fieldName === field.field_name}
                    activeStyle={styles.paletteItemActive}
                    onPress={() => handlePalettePress(payload)}
                  >
                    <View style={styles.paletteItemInner}>
                      <Text style={styles.paletteIcon}>⎔</Text>
                      <View style={styles.paletteFieldText}>
                        <Text style={styles.paletteLabel}>
                          {field.label || field.field_name}
                        </Text>
                        <Text style={styles.paletteFieldMeta}>{field.field_name}</Text>
                      </View>
                    </View>
                  </PaletteDraggable>
                );
              })
            )}
          </ScrollView>

          <View style={styles.leftActions}>
            <TouchableOpacity
              style={styles.defaultAllBtn}
              onPress={handleApplyDefaultToAll}
              disabled={saving}
            >
              <Text style={styles.defaultAllBtnText}>Make default for all objects</Text>
            </TouchableOpacity>
            <View style={styles.cancelSaveRow}>
              <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel} disabled={saving}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveBtn, (!isDirty || saving) && styles.saveBtnDisabled]}
                onPress={() => handleSave()}
                disabled={!isDirty || saving}
              >
                <Text style={styles.saveBtnText}>{saving ? 'Saving…' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <View style={styles.canvasPane}>
          {loadingObjects || loadingLayout ? (
            <View style={styles.canvasEmpty}>
              <ActivityIndicator size="large" color="#d4af37" />
              <Text style={styles.canvasEmptyText}>Loading layout…</Text>
            </View>
          ) : (
            <ScrollView
              style={styles.canvasScroll}
              contentContainerStyle={styles.canvasScrollContent}
            >
              {layout.rows.length === 0 ? (
                <CanvasDropTarget
                  isEmptyZone
                  style={styles.canvasDropZoneEmpty}
                  onPress={() =>
                    nativePickPayload && handleDropOnEmptyCanvas(nativePickPayload)
                  }
                >
                  <Text style={styles.canvasEmptyText}>
                    Drag widgets here to build the record page
                  </Text>
                  <Text style={styles.canvasEmptySub}>
                    Or add a row from the left pane, then drop into columns
                  </Text>
                </CanvasDropTarget>
              ) : (
                layout.rows.map((row) => {
                  const preset = LAYOUT_PRESETS[row.layoutKey] || LAYOUT_PRESETS.full;
                  return (
                    <View key={row.id} style={styles.canvasRow}>
                      <View style={styles.canvasRowHeader}>
                        <Text style={styles.canvasRowLabel}>
                          {preset.label}
                        </Text>
                        <TouchableOpacity
                          onPress={() =>
                            setLayout((prev) => ({
                              ...normalizeLayout(prev),
                              rows: normalizeLayout(prev).rows.filter((r) => r.id !== row.id),
                            }))
                          }
                        >
                          <Text style={styles.canvasRowRemove}>Remove row</Text>
                        </TouchableOpacity>
                      </View>
                      <View style={styles.canvasRowColumns}>
                        {row.columns.map((col, colIdx) => (
                          <View
                            key={`${row.id}-col-${colIdx}`}
                            style={[
                              styles.canvasColumn,
                              { flex: preset.columns[colIdx] || 1 },
                              colIdx < row.columns.length - 1 && styles.canvasColumnGap,
                            ]}
                          >
                            <CanvasDropTarget
                              dropRowId={row.id}
                              dropColumnIndex={colIdx}
                              style={styles.canvasColumnInner}
                              highlightStyle={styles.canvasColumnDropActive}
                              onPress={() => handleColumnPress(row.id, colIdx)}
                            >
                              {col.widgets.length === 0 ? (
                                <Text style={styles.columnPlaceholder}>Drop here</Text>
                              ) : (
                                col.widgets.map((w) =>
                                  renderWidget(w, row.id, colIdx)
                                )
                              )}
                            </CanvasDropTarget>
                          </View>
                        ))}
                      </View>
                    </View>
                  );
                })
              )}
            </ScrollView>
          )}
        </View>
      </View>
      </RecordLayoutDragProvider>

      <Modal visible={showUnsavedModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Unsaved changes</Text>
            <Text style={styles.modalBody}>
              Save your layout before leaving or switching objects?
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalBtnSecondary}
                onPress={() => {
                  setShowUnsavedModal(false);
                  setPendingObjectApiName(null);
                  pendingNavActionRef.current = null;
                }}
              >
                <Text style={styles.modalBtnSecondaryText}>Stay</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnSecondary} onPress={handleUnsavedDiscard}>
                <Text style={styles.modalBtnDangerText}>Discard</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnPrimary} onPress={handleUnsavedSave}>
                <Text style={styles.modalBtnPrimaryText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!editingWidget} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Edit widget</Text>
            {editingWidget?.type === 'section' ? (
              <TextInput
                style={styles.modalInput}
                value={editingWidget.label || ''}
                onChangeText={(t) =>
                  setEditingWidget((w) => ({ ...w, label: t }))
                }
                placeholder="Section title"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              />
            ) : null}
            {editingWidget?.type === 'text' ? (
              <TextInput
                style={[styles.modalInput, styles.modalInputMultiline]}
                value={editingWidget.content || ''}
                onChangeText={(t) =>
                  setEditingWidget((w) => ({ ...w, content: t }))
                }
                placeholder="Text content"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                multiline
              />
            ) : null}
            {editingWidget?.type === 'image' ? (
              <TextInput
                style={styles.modalInput}
                value={editingWidget.imageUrl || ''}
                onChangeText={(t) =>
                  setEditingWidget((w) => ({ ...w, imageUrl: t }))
                }
                placeholder="Image URL"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                autoCapitalize="none"
              />
            ) : null}
            {editingWidget?.type === 'field' ? (
              <Text style={styles.modalBody}>
                Field: {editingWidget.label} ({editingWidget.fieldName})
              </Text>
            ) : null}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalBtnSecondary}
                onPress={() => setEditingWidget(null)}
              >
                <Text style={styles.modalBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalBtnPrimary}
                onPress={() => {
                  if (!editingWidget) return;
                  const { rowId, columnIndex, ...patch } = editingWidget;
                  setLayout((prev) =>
                    updateWidgetInLayout(prev, rowId, columnIndex, patch.id, patch)
                  );
                  setEditingWidget(null);
                }}
              >
                <Text style={styles.modalBtnPrimaryText}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => {
  const muted = theme.isDarkMode ? '#888888' : '#666666';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#f5f5f5',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 12,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    layoutNameSub: {
      fontSize: 12,
      color: '#d4af37',
      marginTop: 2,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    objectSelectorRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingBottom: 10,
      gap: 10,
      zIndex: 20,
      backgroundColor: 'transparent',
    },
    objectSelectorLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    objectSelectorTrigger: {
      flex: 1,
      maxWidth: 320,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 2,
      borderColor: '#d4af37',
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
    },
    objectSelectorValue: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    objectSelectorChevron: {
      color: '#d4af37',
      fontSize: 14,
      backgroundColor: 'transparent',
    },
    unsavedBadge: {
      fontSize: 12,
      color: '#c62828',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    objectDropdown: {
      position: 'absolute',
      top: 118,
      left: 20,
      right: 20,
      maxWidth: 360,
      maxHeight: 220,
      zIndex: 30,
      borderWidth: 2,
      borderColor: '#d4af37',
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }
        : {}),
    },
    objectDropdownScroll: {
      maxHeight: 220,
    },
    objectDropdownItem: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    objectDropdownItemActive: {
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.12)' : 'rgba(212,175,55,0.15)',
    },
    objectDropdownItemText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    objectDropdownItemMeta: {
      fontSize: 11,
      color: muted,
      backgroundColor: 'transparent',
    },
    mainRow: {
      flex: 1,
      flexDirection: 'row',
      paddingHorizontal: 12,
      paddingBottom: 12,
      gap: 12,
    },
    leftPane: {
      width: 280,
      borderWidth: 2,
      borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
      borderRadius: theme.isDarkMode ? 0 : 10,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
      overflow: 'hidden',
    },
    leftScroll: {
      flex: 1,
      padding: 12,
    },
    panelSectionTitle: {
      fontSize: 13,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.colors.heading,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    panelSectionSpaced: {
      marginTop: 16,
    },
    panelHint: {
      fontSize: 12,
      color: muted,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    layoutOption: {
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 6,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    layoutOptionActive: {
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.15)' : 'rgba(212,175,55,0.2)',
    },
    layoutOptionLabel: {
      fontSize: 13,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    layoutPreviewRow: {
      flexDirection: 'row',
      marginTop: 8,
      marginBottom: 12,
      minHeight: 48,
      backgroundColor: 'transparent',
    },
    layoutPreviewCell: {
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: '#d4af37',
      borderRadius: 4,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      backgroundColor: 'transparent',
    },
    layoutPreviewCellGap: {
      marginRight: 6,
    },
    layoutPreviewLabel: {
      fontSize: 11,
      color: muted,
      backgroundColor: 'transparent',
    },
    addRowBtn: {
      borderWidth: 1,
      borderColor: '#d4af37',
      borderRadius: 6,
      paddingVertical: 8,
      alignItems: 'center',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    addRowBtnText: {
      fontSize: 13,
      fontWeight: '600',
      color: '#d4af37',
      backgroundColor: 'transparent',
    },
    paletteItem: {
      marginBottom: 6,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#fafafa',
    },
    paletteItemActive: {
      borderColor: '#d4af37',
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.12)' : 'rgba(212,175,55,0.12)',
    },
    paletteItemInner: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 10,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' ? { pointerEvents: 'none' } : {}),
    },
    paletteIcon: {
      fontSize: 18,
      marginRight: 10,
      backgroundColor: 'transparent',
    },
    paletteLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    paletteFieldText: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    paletteFieldMeta: {
      fontSize: 11,
      color: muted,
      backgroundColor: 'transparent',
    },
    fieldsLoading: {
      marginVertical: 12,
    },
    nativePickHint: {
      fontSize: 12,
      color: '#d4af37',
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    leftActions: {
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      padding: 10,
      backgroundColor: 'transparent',
    },
    defaultAllBtn: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 6,
      paddingVertical: 10,
      alignItems: 'center',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    defaultAllBtnText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    cancelSaveRow: {
      flexDirection: 'row',
      gap: 8,
      backgroundColor: 'transparent',
    },
    cancelBtn: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.colors.border,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    cancelBtnText: {
      color: theme.colors.text,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    saveBtn: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 6,
      backgroundColor: '#d4af37',
      alignItems: 'center',
    },
    saveBtnDisabled: {
      opacity: 0.45,
    },
    saveBtnText: {
      color: '#000',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    canvasPane: {
      flex: 1,
      borderWidth: 2,
      borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
      borderRadius: theme.isDarkMode ? 0 : 10,
      backgroundColor: theme.isDarkMode ? '#111' : '#fff',
      overflow: 'hidden',
    },
    canvasScroll: {
      flex: 1,
    },
    canvasScrollContent: {
      padding: 16,
      paddingBottom: 40,
    },
    canvasEmpty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    canvasDropZoneEmpty: {
      minHeight: 200,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: '#d4af37',
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundColor: 'transparent',
    },
    canvasEmptyText: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    canvasEmptySub: {
      fontSize: 13,
      color: muted,
      marginTop: 8,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    canvasRow: {
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    canvasRowHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    canvasRowLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: muted,
      backgroundColor: 'transparent',
    },
    canvasRowRemove: {
      fontSize: 12,
      color: '#c62828',
      backgroundColor: 'transparent',
    },
    canvasRowColumns: {
      flexDirection: 'row',
      backgroundColor: 'transparent',
    },
    canvasColumn: {
      minHeight: 100,
      backgroundColor: 'transparent',
    },
    canvasColumnGap: {
      marginRight: 8,
    },
    canvasColumnHighlight: {
      opacity: 0.85,
    },
    canvasColumnInner: {
      flex: 1,
      width: '100%',
      alignSelf: 'stretch',
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: theme.isDarkMode ? '#555' : '#ccc',
      borderRadius: 6,
      padding: 8,
      minHeight: 100,
      backgroundColor: theme.isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
    canvasColumnDropActive: {
      borderColor: '#d4af37',
      backgroundColor: theme.isDarkMode
        ? 'rgba(212, 175, 55, 0.12)'
        : 'rgba(212, 175, 55, 0.14)',
    },
    columnPlaceholder: {
      fontSize: 12,
      color: muted,
      textAlign: 'center',
      marginTop: 32,
      backgroundColor: 'transparent',
    },
    canvasWidget: {
      width: '100%',
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: '#d4af37',
      borderRadius: 6,
      padding: 10,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fffef8',
    },
    canvasWidgetMoving: {
      borderColor: '#d4af37',
      backgroundColor: theme.isDarkMode
        ? 'rgba(212, 175, 55, 0.12)'
        : 'rgba(212, 175, 55, 0.14)',
    },
    widgetHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    widgetType: {
      fontSize: 10,
      fontWeight: '700',
      textTransform: 'uppercase',
      color: '#d4af37',
      backgroundColor: 'transparent',
    },
    widgetRemove: {
      fontSize: 14,
      color: '#c62828',
      backgroundColor: 'transparent',
    },
    widgetTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    widgetMeta: {
      fontSize: 11,
      color: muted,
      backgroundColor: 'transparent',
    },
    widgetBody: {
      fontSize: 13,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalCard: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
      borderRadius: 10,
      padding: 20,
      borderWidth: 2,
      borderColor: '#d4af37',
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    modalBody: {
      fontSize: 14,
      color: theme.colors.text,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    modalInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 8,
      color: theme.colors.text,
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#fff',
    },
    modalInputMultiline: {
      minHeight: 80,
      textAlignVertical: 'top',
    },
    modalActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 8,
      flexWrap: 'wrap',
      backgroundColor: 'transparent',
    },
    modalBtnSecondary: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    modalBtnSecondaryText: {
      color: theme.colors.text,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    modalBtnDangerText: {
      color: '#c62828',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    modalBtnPrimary: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 6,
      backgroundColor: '#d4af37',
    },
    modalBtnPrimaryText: {
      color: '#000',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    deniedText: {
      margin: 40,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    linkText: {
      marginHorizontal: 40,
      color: '#d4af37',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
  });
};
